import React from 'react'
import { ICellRendererParams } from 'ag-grid-community'
import { NumberCellRenderer } from '../../../containers/BulkSheetView/components/cellRenderer/NumberCellRenderer'
import CustomTooltip from '../../../components/tooltips/Tooltip/CustomTooltip'
import {
  getVarianceInfo,
  getVarianceTargetRow,
  getTaskVarianceInfo,
  getTotalTaskVarianceInfo,
  getSprintEstimatedHourList,
  getSprintTaskEstimatedHourList,
  SprintEstimatedHourInfo,
} from '../gridOptions/varianceUtils'
import { intl } from '../../../../i18n'
import Workload, {
  getWorkloadUnitValue,
  WorkloadUnit,
} from '../../../../lib/functions/workload'

// 工数単位のラベルを取得
const getWorkloadUnitLabel = (workloadUnit: WorkloadUnit): string => {
  switch (workloadUnit) {
    case WorkloadUnit.MONTH:
      return intl.formatMessage({ id: 'workloadUnit.month' })
    case WorkloadUnit.DAY:
      return intl.formatMessage({ id: 'workloadUnit.day' })
    case WorkloadUnit.HOUR:
    default:
      return intl.formatMessage({ id: 'workloadUnit.hour' })
  }
}

// 時間を選択中の単位に変換
const convertHourToUnit = (
  hour: number,
  workloadUnit: WorkloadUnit,
  workloadStandard: { dailyWorkHours: number; monthlyWorkDays: number }
): number => {
  const workload = Workload.from({
    hour,
    standard: workloadStandard,
  })
  return getWorkloadUnitValue(workload, workloadUnit)
}

// contextから工数単位の設定を取得するヘルパー
const getWorkloadSettings = (context: ICellRendererParams['context']) => {
  const workloadUnit =
    context?.workloadUnitState?.selectedWorkloadUnit || WorkloadUnit.HOUR
  const workloadStandard = context?.workloadUnitState || {
    dailyWorkHours: 1,
    monthlyWorkDays: 1,
  }
  const unitLabel = getWorkloadUnitLabel(workloadUnit)
  return { workloadUnit, workloadStandard, unitLabel }
}

// スプリント内訳を文字列に変換
const formatSprintBreakdown = (
  sprintBreakdown: SprintEstimatedHourInfo[],
  workloadUnit: WorkloadUnit,
  workloadStandard: { dailyWorkHours: number; monthlyWorkDays: number },
  unitLabel: string
): string => {
  if (sprintBreakdown.length === 0) return ''

  return sprintBreakdown
    .map(sprint => {
      const value = convertHourToUnit(
        sprint.estimatedHour,
        workloadUnit,
        workloadStandard
      )
      return `  ${sprint.sprintName}: ${value}${unitLabel}`
    })
    .join('\n')
}

// 統一ツールチップテキストを生成（成果物見積もり + タスク見積もり のスプリント別内訳）
const buildVarianceTooltip = (
  row: NonNullable<ReturnType<typeof getVarianceTargetRow>>,
  context: ICellRendererParams['context']
): string => {
  const { workloadUnit, workloadStandard, unitLabel } =
    getWorkloadSettings(context)

  const deliverableBreakdown = getSprintEstimatedHourList(row)
  const taskBreakdown = getSprintTaskEstimatedHourList(row)

  const deliverableHeader = intl.formatMessage({
    id: 'productBacklog.estimatedWorkload.variance.tooltip.deliverableHeader',
  })
  const taskHeader = intl.formatMessage({
    id: 'productBacklog.estimatedWorkload.variance.tooltip.taskHeader',
  })

  const deliverableLines = formatSprintBreakdown(
    deliverableBreakdown,
    workloadUnit,
    workloadStandard,
    unitLabel
  )
  const taskLines = formatSprintBreakdown(
    taskBreakdown,
    workloadUnit,
    workloadStandard,
    unitLabel
  )

  return [
    `${deliverableHeader}\n${deliverableLines}`,
    `${taskHeader}\n${taskLines}`,
  ].join('\n\n')
}

// 統一ツールチップテキストを生成（成果物合計 vs タスク合計）
const buildTotalVarianceTooltip = (
  row: NonNullable<ReturnType<typeof getVarianceTargetRow>>,
  context: ICellRendererParams['context']
): string => {
  const { workloadUnit, workloadStandard, unitLabel } =
    getWorkloadSettings(context)

  const { deliverableHour, taskHour } = getTotalTaskVarianceInfo(row)

  const deliverableValue = convertHourToUnit(
    deliverableHour,
    workloadUnit,
    workloadStandard
  )
  const taskValue = convertHourToUnit(taskHour, workloadUnit, workloadStandard)

  const deliverableLabel = intl.formatMessage({
    id: 'productBacklog.estimatedWorkload.variance.tooltip.deliverableTotalLabel',
  })
  const taskLabel = intl.formatMessage({
    id: 'productBacklog.estimatedWorkload.variance.tooltip.taskTotalLabel',
  })

  return `${deliverableLabel} ${deliverableValue}${unitLabel}\n${taskLabel} ${taskValue}${unitLabel}`
}

// 乖離セルレンダラーのファクトリ
type VarianceRow = NonNullable<ReturnType<typeof getVarianceTargetRow>>
const createVarianceRenderer =
  (
    checkVariance: (row: VarianceRow) => boolean,
    buildTooltip: (
      row: VarianceRow,
      context: ICellRendererParams['context']
    ) => string
  ): React.FC<ICellRendererParams> =>
  props => {
    const row = getVarianceTargetRow(props.data)
    const isHighlighted = row ? checkVariance(row) : false
    const tooltipValue =
      isHighlighted && row ? buildTooltip(row, props.context) : ''

    if (!tooltipValue) {
      return <NumberCellRenderer {...props} />
    }
    return (
      <CustomTooltip value={tooltipValue}>
        <NumberCellRenderer {...props} />
      </CustomTooltip>
    )
  }

// 成果物列用: 成果物vsΣスプリント or 成果物vsタスク合計
export const DeliverableTotalCellRenderer = createVarianceRenderer(
  row =>
    getVarianceInfo(row).hasVariance ||
    getTotalTaskVarianceInfo(row).hasVariance,
  buildTotalVarianceTooltip
)

// 成果物（スプリント）列用: 成果物vsΣスプリント or スプリントvsタスクスプリント
const EstimatedWorkloadCellRenderer = createVarianceRenderer(
  row =>
    getVarianceInfo(row).hasVariance || getTaskVarianceInfo(row).hasVariance,
  buildVarianceTooltip
)

// タスク列用: 成果物vsタスク合計
export const TotalTaskEstimatedWorkloadCellRenderer = createVarianceRenderer(
  row => getTotalTaskVarianceInfo(row).hasVariance,
  buildTotalVarianceTooltip
)

// タスク（スプリント）列用: スプリントvsタスクスプリント
export const TaskEstimatedWorkloadCellRenderer = createVarianceRenderer(
  row => getTaskVarianceInfo(row).hasVariance,
  buildVarianceTooltip
)

export default EstimatedWorkloadCellRenderer
